import { useCallback, useEffect, useReducer, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import { streamChatMessage } from './chatStream';
import {
  initialTranscriptState,
  transcriptReducer,
  type ChatApprovalDecision,
  type TranscriptState,
} from './chatTranscript';

/**
 * Quick-chat controller hook (§21, WP-C4). Owns the streaming transcript (a
 * `useReducer` — this is live turn state, NOT query-cached server state), loads
 * prior turns for the persisted session on open (TanStack Query → folded into the
 * reducer once), streams each sent turn over the POST, and relays inline approval
 * decisions on a separate request that unblocks the SAME open stream.
 *
 * The active session id is persisted per-workspace in `localStorage` so a reload
 * resumes the last conversation (the pinned contract's optional "most-recent
 * session" list endpoint is not required — we never block on it).
 */

function sessionStorageKey(workspaceId: string): string {
  return `ehe.chat.session.${workspaceId}`;
}

function readStoredSession(workspaceId: string): string | null {
  try {
    return localStorage.getItem(sessionStorageKey(workspaceId));
  } catch {
    return null;
  }
}

function writeStoredSession(workspaceId: string, sessionId: string | null): void {
  try {
    if (sessionId) localStorage.setItem(sessionStorageKey(workspaceId), sessionId);
    else localStorage.removeItem(sessionStorageKey(workspaceId));
  } catch {
    /* private mode / disabled storage — session stays in memory only */
  }
}

export interface UseChatResult {
  state: TranscriptState;
  /** True once history for the persisted session is loading. */
  historyLoading: boolean;
  /** Send a turn. No-op while a turn is already streaming or content is blank. */
  send: (content: string) => void;
  /** Approve/reject an inline approval; the open turn then continues. */
  decide: (approvalId: string, decision: ChatApprovalDecision) => void;
  /**
   * Start a FRESH conversation: clears the persisted session id + empties the
   * transcript, so the next `send` omits `sessionId` and the server cold-starts a
   * new session (no resume). The user-facing escape hatch when a session is stuck —
   * e.g. its underlying SDK conversation was wiped by a redeploy (R1).
   */
  newSession: () => void;
}

export function useChat(workspaceId: string, open: boolean): UseChatResult {
  const [state, dispatch] = useReducer(transcriptReducer, initialTranscriptState, (init) => ({
    ...init,
    sessionId: readStoredSession(workspaceId),
  }));

  // Latest state for callbacks (avoids stale closures over sessionId/streaming).
  const stateRef = useRef(state);
  stateRef.current = state;

  const storedSessionId = readStoredSession(workspaceId);

  // Rehydrate prior turns once, when the panel opens for a known session and the
  // transcript is still empty. Query owns the fetch; the reducer owns the transcript.
  const history = useQuery({
    queryKey: ['chatMessages', workspaceId, storedSessionId],
    queryFn: () => api.getChatSessionMessages(workspaceId, storedSessionId as string),
    enabled: open && !!storedSessionId,
    retry: false,
  });

  const loadedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!storedSessionId) return;
    if (history.data && loadedRef.current !== storedSessionId && state.items.length === 0) {
      loadedRef.current = storedSessionId;
      dispatch({ type: 'load_history', sessionId: storedSessionId, messages: history.data });
    }
  }, [history.data, storedSessionId, state.items.length]);

  // A stored session the server no longer knows (404) — drop it and start fresh.
  useEffect(() => {
    if (storedSessionId && history.isError) writeStoredSession(workspaceId, null);
  }, [history.isError, storedSessionId, workspaceId]);

  // Persist the (possibly newly minted) session id whenever it changes.
  useEffect(() => {
    if (state.sessionId) writeStoredSession(workspaceId, state.sessionId);
  }, [state.sessionId, workspaceId]);

  const send = useCallback(
    (content: string) => {
      const trimmed = content.trim();
      if (!trimmed || stateRef.current.streaming) return;
      dispatch({ type: 'send', id: crypto.randomUUID(), content: trimmed });
      void streamChatMessage({
        workspaceId,
        token: api.token,
        body: {
          content: trimmed,
          sessionId: stateRef.current.sessionId ?? undefined,
        },
        onEvent: (event) => dispatch({ type: 'stream_event', event }),
      }).catch((err: unknown) => {
        const message = err instanceof Error ? err.message : 'The assistant is unavailable.';
        dispatch({ type: 'stream_error', error: message });
      });
    },
    [workspaceId],
  );

  const decide = useCallback(
    (approvalId: string, decision: ChatApprovalDecision) => {
      // Optimistically disable the buttons; the turn continues on the open stream.
      dispatch({ type: 'approval_decided', approvalId, decision });
      api.decideChatApproval(workspaceId, approvalId, decision).catch(() => {
        // Let the user retry if the decision request itself failed.
        dispatch({ type: 'approval_reset', approvalId });
      });
    },
    [workspaceId],
  );

  const newSession = useCallback(() => {
    // Drop the persisted ref and clear the transcript. The `state.sessionId`-persist
    // effect above only WRITES a truthy id, so it won't re-add the removed one; the
    // next `send` sees `sessionId: null` and omits it → the server creates a fresh
    // session (cold start). Reset the rehydrate guard so a later known session can
    // still load its history.
    writeStoredSession(workspaceId, null);
    loadedRef.current = null;
    dispatch({ type: 'reset' });
  }, [workspaceId]);

  return { state, historyLoading: history.isLoading, send, decide, newSession };
}
